
// Service worker registration utility

import { getNetworkStatus } from './networkMonitor';

/**
 * Register the service worker for production environment
 * Now with connection-aware logic to defer registration on slow connections
 */
export function registerServiceWorker(force = false) {
  if ('serviceWorker' in navigator) {
    // Allow registration in development and preview for PWA testing
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isProduction = process.env.NODE_ENV === 'production';
    const isPreview = window.location.hostname.includes('lovable');
    
    // Always allow in preview mode for PWA testing
    if (!isDevelopment && !isProduction && !isPreview && !force) {
      console.log('Service worker registration skipped - not in dev, prod, or preview mode');
      return;
    }
    
    console.log('Service worker registration environment:', { isDevelopment, isProduction, isPreview, force });
    
    // In production, register immediately for PWA functionality
    if (isProduction || isPreview) {
      console.log('Production/Preview mode - registering service worker immediately');
      if (document.readyState === 'loading') {
        window.addEventListener('load', () => {
          setTimeout(performRegistration, 500);
        });
      } else {
        setTimeout(performRegistration, 500);
      }
      return;
    }
    
    // Get network status only in development for testing
    if (isDevelopment) {
      const networkStatus = getNetworkStatus();
      
      // Check if we're on a very slow connection
      const isVerySlowConnection = 
        networkStatus.effectiveType === 'slow-2g' || 
        networkStatus.effectiveType === '2g';
      
      // For very slow connections, we'll defer service worker registration
      // unless explicitly forced
      const shouldDefer = isVerySlowConnection && !force;
      
      if (shouldDefer) {
        console.log('Deferring service worker registration due to slow connection');
        
        // Listen for idle time or when the page becomes visible again
        const registerLater = () => {
          // Remove the listeners once we register
          document.removeEventListener('visibilitychange', visibilityHandler);
          window.removeEventListener('load', loadHandler);
          
          // Now register the service worker
          console.log('Deferred service worker registration starting now');
          performRegistration();
        };
        
        // Handler for when page becomes visible again
        const visibilityHandler = () => {
          if (document.visibilityState === 'visible') {
            registerLater();
          }
        };
        
        // Handler for when page is fully loaded
        const loadHandler = () => {
          // Wait a bit after load to prioritize rendering
          setTimeout(registerLater, 10000);
        };
        
        // Add event listeners
        document.addEventListener('visibilitychange', visibilityHandler);
        window.addEventListener('load', loadHandler);
        
        return;
      }
      
      // For normal connections, register as usual but still after a short delay
      window.addEventListener('load', () => {
        setTimeout(() => {
          performRegistration();
        }, isVerySlowConnection ? 5000 : 1000);
      });
    } else {
      // In development, register immediately for testing
      console.log('Development mode - registering service worker for PWA testing');
      if (document.readyState === 'loading') {
        window.addEventListener('load', () => {
          setTimeout(performRegistration, 1000);
        });
      } else {
        setTimeout(performRegistration, 1000);
      }
    }
  }
}

/**
 * The actual service worker registration logic
 */
function performRegistration() {
  const swUrl = '/service-worker.js';
  
  console.log('Attempting to register service worker at:', swUrl);
  
  navigator.serviceWorker.register(swUrl, {
    scope: '/',
    updateViaCache: 'none' // Always check for updates
  })
    .then(registration => {
      console.log('ServiceWorker registration successful with scope: ', registration.scope);
      
      // Listen for install prompt events after service worker is ready
      registration.addEventListener('updatefound', () => {
        console.log('Service worker update found');
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            console.log('Service worker state changed to:', newWorker.state);
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('New service worker installed, app ready for PWA install');
            }
          });
        }
      });
      
      // Check for updates less frequently to reduce network usage
      // Only check on page load, then every 6 hours
      setTimeout(() => {
        registration.update();
      }, 10 * 60 * 1000); // Wait 10 minutes after load before first update check
      
      setInterval(() => {
        registration.update();
      }, 6 * 60 * 60 * 1000); // Check every 6 hours
    })
    .catch(error => {
      console.error('Error during service worker registration:', error);
      console.error('Service worker file may be missing or invalid');
    });
    
  // Handle module loading failures
  if (!window.__moduleErrorListenerAdded) {
    window.__moduleErrorListenerAdded = true;
    
    window.addEventListener('error', (event) => {
      const errorText = event.message || '';
      const errorSrc = event.filename || '';
      
      // Check if the error is related to module loading
      if (
        (errorText.includes('Failed to fetch dynamically imported module') || 
         errorText.includes('ChunkLoadError') ||
         errorText.includes('Loading chunk')) &&
        !sessionStorage.getItem('module_load_error')
      ) {
        console.error('Module loading error detected:', errorText);
        
        // Set a flag to prevent multiple alerts
        sessionStorage.setItem('module_load_error', 'true');
        
        // Show an error message to the user
        alert('There was an error loading the application. Please refresh the page to try again.');
        
        // Attempt to unregister problematic service worker
        navigator.serviceWorker.getRegistrations().then(registrations => {
          for(let registration of registrations) {
            registration.unregister();
            console.log('Unregistered service worker to recover from module loading error');
          }
        });
      }
    });
  }
}

/**
 * Unregister all service workers
 */
export function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => {
        registration.unregister();
      })
      .catch(error => {
        console.error(error.message);
      });
  }
}

/**
 * Invalidate the cache for a specific image URL
 */
export function invalidateImageCache(imageUrl: string) {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'INVALIDATE_IMAGE_CACHE',
      url: imageUrl
    });
    return true;
  }
  return false;
}

/**
 * Clear the entire image cache
 * Use sparingly - this will force all images to reload from network
 */
export function clearImageCache() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEAR_IMAGE_CACHE'
    });
    return true;
  }
  return false;
}

/**
 * Force reload an image by creating a new Image object with cache buster
 */
export function forceReloadImage(imgElement: HTMLImageElement | null, src: string) {
  if (!imgElement) return false;
  
  // First invalidate the cache
  invalidateImageCache(src);
  
  // Add cache buster and reload
  const timestamp = Date.now();
  const cacheBuster = src.includes('?') ? `&t=${timestamp}` : `?t=${timestamp}`;
  imgElement.src = src + cacheBuster;
  
  return true;
}

// Add TypeScript interface for window to allow the module error listener flag
declare global {
  interface Window {
    __moduleErrorListenerAdded?: boolean;
  }
}
